const variantDropdownTemplateHTML = `
<div class="dropdown">
  <button
    type="button"
    class="js-dropdown-toggle dropdown-toggle no-styles"
    role="combobox"
    aria-haspopup="listbox"
    aria-controls="dropdown-content"
    aria-expanded="false"
    aria-labelledby="variant-dropdown-label"
  >
    <slot name="label"></slot>
    <span class="icon" aria-hidden="true">
      <slot name="icon"></slot>
    </span>
  </button>
  <div class="selected-variant"></div>
  <div class="dropdown-content" id="dropdown-content">
    <slot name="dropdown-list"></slot>
  </div>
</div>`

class VariantSwatchDropdown extends HHBaseElement {
  constructor() {
    super(variantDropdownTemplateHTML, 'closed')
    this.isReady = false
    this._bindMethods()
    this._cacheDom()
    this._cacheVariants()
    this._attachGlobalListeners()
  }

  _bindMethods() {
    this._toggleDropdown = this._toggleDropdown.bind(this)
    this._navigateByArrows = this._navigateByArrows.bind(this)
    this._handleSelectChange = this._handleSelectChange.bind(this)
    this._handleSelectOption = this._handleSelectOption.bind(this)
    this._handleClickOutside = this._handleClickOutside.bind(this)
    this._updateBundleOption = this._updateBundleOption.bind(this)
    this._checkFocus = this._checkFocus.bind(this)
  }

  _cacheDom() {
    const shadow = this._shadowRoot
    this._dropDownList = this.querySelector('[slot="dropdown-list"]')
    this._dropdownMenu = shadow.querySelector('.dropdown-content')
    this._dropDownToggle = shadow.querySelector('.js-dropdown-toggle')
    this.selectedVariant = shadow.querySelector('.selected-variant')
  }

  _cacheVariants() {
    this.mainProduct = this.closest('main-product')
    this.productVariants = this.closest('product-variants')
    this._dropDownOptionLis = this._dropDownList ? Array.from(this._dropDownList.querySelectorAll('li[role="option"]')) : []
    this._dropdownOptions = this._dropDownList ? Array.from(this._dropDownList.querySelectorAll('.dropdown-option')) : []
    this.allColorSwatches = this.mainProduct ? Array.from(this.mainProduct.querySelectorAll('.color-variants input')) : []
    this.allSizeButtons = this.mainProduct ? Array.from(this.mainProduct.querySelectorAll('.size-variants-container .size-select-container button')) : []
    this.otherVariantDropdown = this.productVariants ? this.productVariants.querySelector('.general-variants-container') : null
  }

  _attachGlobalListeners() {
    if (!VariantSwatchDropdown._globalListenersAttached) {
      document.addEventListener('focusin', VariantSwatchDropdown._handleGlobalFocus)
      window.addEventListener('click', VariantSwatchDropdown._handleGlobalClick)
      window.addEventListener('keydown', VariantSwatchDropdown._handleGlobalClick)
      VariantSwatchDropdown._globalListenersAttached = true
    }
  }

  connectedCallback() {
    this.isReady = true
    this._attachListeners()
    this._attachOptionsListeners()
    if (this.mainProduct && typeof this.mainProduct.initBundle === 'function') {
      this.mainProduct.initBundle()
    }
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  open() {
    this.setAttribute('open', true)
    if (this._dropDownToggle) this._dropDownToggle.setAttribute('aria-expanded', true)
  }

  close() {
    this.removeAttribute('open')
    if (this._dropDownToggle) this._dropDownToggle.setAttribute('aria-expanded', false)
  }

  get usesNativeSelect() {
    return this.isiOSOrAndroid || !this.mql?.matches
  }

  get isOpen() {
    return this.getAttribute('open')
  }

  _checkFocus() {
    if (this.isOpen && this._dropdownOptions) {
      for (const item of this._dropdownOptions) {
        const input = item.querySelector('.input')
        item.classList.toggle('selected', input === document.activeElement)
      }
    }
  }

  get isBundle() {
    return this.getAttribute('data-is-bundle') === 'true'
  }

  get optionTarget() {
    return this.getAttribute('data-option-target')
  }

  set variantName(newValue) {
    let newVariantId = null
    if (this.productVariants) {
      const liquidOptions = this.productVariants.querySelectorAll('.hidden-variants option')
      for (const liquidOption of liquidOptions) {
        if (newValue === liquidOption.innerText) {
          newVariantId = liquidOption.value
          break
        }
      }
    }
    if (!newVariantId) return
    this.variant = newVariantId
  }

  set variant(newValue) {
    this._clearSelectedDropdownInputs()
    if (!this._dropdownOptions) return
    let matchingNewVariants = this._dropdownOptions.filter(option => {
      const input = option.querySelector('.input')
      return input && input.dataset.variantId === newValue
    })
    for (const item of this._dropdownOptions) {
      item.classList.remove('selected')
      if (item.parentElement) {
        item.parentElement.classList.remove('selected', 'focussed')
        item.parentElement.removeAttribute('aria-selected')
      }
    }
    if (matchingNewVariants.length > 0) {
      let newVariantClone = matchingNewVariants[0].cloneNode(true)
      this.selectedVariant.innerHTML = ''
      this.selectedVariant.append(newVariantClone)
      for (const label of matchingNewVariants) {
        const input = label.querySelector('.input')
        if (input) input.setAttribute('checked', 'true')
        label.classList.add('selected')
        if (label.parentElement) {
          label.parentElement.classList.add('selected', 'focussed')
          label.parentElement.setAttribute('aria-selected', 'true')
          if (this._dropDownToggle) {
            this._dropDownToggle.setAttribute('aria-activedescendant', label.parentElement.id)
            this._dropDownToggle.setAttribute('aria-labelledby', `variant-dropdown-label ${label.parentElement.id}`)
          }
        }
      }
      if (this.allColorSwatches.length > 0) {
        const btn = this.allColorSwatches.find(button => button.dataset.variantId === newValue)
        if (btn) btn.click()
      } else if (this.allSizeButtons.length > 0) {
        this.selectedVariant.innerHTML = matchingNewVariants[0].dataset.label || ''
        const btn = this.allSizeButtons.find(button => button.dataset.variantId === newValue)
        if (btn) btn.click()
      } else if (this.otherVariantDropdown) {
        this.selectedVariant.innerHTML = matchingNewVariants[0].dataset.label || ''
        this.close()
      }
    }
  }

  _clearSelectedDropdownInputs() {
    if (!this.productVariants) return
    let allCheckedInputs = this.productVariants.querySelectorAll('.dropdown-menu .input[checked]')
    for (const input of allCheckedInputs) {
      input.setAttribute('checked', false)
    }
  }

  _setVariantId(id) {
    this.setAttribute('variant', id)
  }

  _toggleDropdown(event) {
    event.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  _navigateByArrows(event) {
    event.preventDefault()
    if (event.key === 'Enter' || event.key === ' ') {
      this._toggleDropdown(event)
    }
    if (event.key === 'Tab') {
      let nextFocusParent = this.parentElement?.parentElement?.nextElementSibling
      let nextFocusButton = nextFocusParent?.querySelector('button')
      if (nextFocusButton) {
        nextFocusButton.focus()
      } else {
        this.blur()
      }
      if (this.isOpen) {
        this.close()
      }
    }
    if (event.shiftKey && event.key === 'Tab') {
      let visibleColorSwatches = this.allColorSwatches.filter(swatch => !swatch.classList.contains('hidden'))
      let lastColorSwatch = visibleColorSwatches[visibleColorSwatches.length - 1]
      if (lastColorSwatch) lastColorSwatch.focus()
      if (this.isOpen) {
        this.close()
      }
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const keepOpen = this.isOpen
      const optionLis = this._dropDownOptionLis
      const visibleOptionLis = optionLis.filter(li => li.classList.contains('vis'))
      let currentIndex = visibleOptionLis.findIndex(li => li.classList.contains('focussed'))
      optionLis.forEach(li => li.classList.remove('focussed'))
      if (event.key === 'ArrowUp') {
        currentIndex = currentIndex > 0 ? currentIndex - 1 : visibleOptionLis.length - 1
      } else if (event.key === 'ArrowDown') {
        currentIndex = currentIndex < visibleOptionLis.length - 1 ? currentIndex + 1 : 0
      }
      const currentLi = visibleOptionLis[currentIndex]
      if (currentLi) {
        currentLi.classList.add('focussed')
        currentLi.setAttribute('aria-selected', 'true')
        if (this._dropDownToggle) {
          this._dropDownToggle.setAttribute('aria-activedescendant', currentLi.id)
        }
        const newVariantId = currentLi.getAttribute('data-variant-id')
        const newValue = currentLi.getAttribute('data-value')
        this.variant = newVariantId
        this.value = newValue
        if (this.isBundle && this.optionTarget) {
          this._updateBundleOption(newValue)
        }
        this._dropDownToggle.focus()
        if (keepOpen) {
          this.open()
        }
        currentLi.scrollIntoView({block: 'nearest'})
      }
    }
    if (event.key === 'Escape') {
      if (this.isOpen) {
        this.close()
      }
    }
  }

  _handleSelectChange(event) {
    this.value = event.currentTarget.value
    this.close()
  }

  _attachListeners() {
    if (this._dropDownToggle) {
      this._dropDownToggle.addEventListener('click', this._toggleDropdown)
      this._dropDownToggle.addEventListener('keydown', this._navigateByArrows)
    }
  }

  _removeListeners() {
    if (this._dropDownToggle) {
      this._dropDownToggle.removeEventListener('click', this._toggleDropdown)
      this._dropDownToggle.removeEventListener('keydown', this._navigateByArrows)
    }
  }

  _handleSelectOption(event) {
    const value = event.currentTarget.dataset.value
    const id = event.currentTarget.dataset.variantId
    this.value = value
    this.variant = id
    if (this.isBundle && this.optionTarget) {
      this._updateBundleOption(value)
    }
    this.close()
  }

  _updateBundleOption(newValue) {
    const hiddenOptionInput = document.querySelector(`.single-option-selector[data-option='${this.optionTarget}']`)
    if (!hiddenOptionInput) return
    hiddenOptionInput.value = newValue
    hiddenOptionInput.dispatchEvent(new Event('change'))
  }

  _handleClickOutside(event) {
    if (!this.contains(event.target)) this.close()
  }

  _applyDropdownState(state) {
    if (state) {
      this._dropdownMenu.classList.add('show')
    } else {
      this._dropdownMenu.classList.remove('show')
    }
  }

  _attachOptionsListeners() {
    if (!this._dropdownOptions) return
    for (const dropdownOption of this._dropdownOptions) {
      dropdownOption.addEventListener('click', this._handleSelectOption)
    }
  }

  static get observedAttributes() {
    return ['name', 'id', 'placeholder', 'open', 'variant']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'variant') {
      this.variant = newValue
    }
    if (name === 'open') {
      this._applyDropdownState(newValue === 'true')
    }
  }

  // Static global event handlers for focus and click outside
  static _handleGlobalFocus(event) {
    const dropdowns = document.querySelectorAll('variant-dropdown[open]')
    for (const dropdown of dropdowns) {
      if (dropdown._checkFocus) dropdown._checkFocus(event)
    }
  }
  static _handleGlobalClick(event) {
    const dropdowns = document.querySelectorAll('variant-dropdown[open]')
    for (const dropdown of dropdowns) {
      if (!dropdown.contains(event.target) && dropdown.close) dropdown.close()
    }
  }
}

VariantSwatchDropdown._globalListenersAttached = false

window.customElements.define('variant-dropdown', VariantSwatchDropdown)
